import { audioCtx } from "./context.js";
import { globalSfxVolume } from "./sfx.js";

/** Synthesize a massive, realistic military tank cannon blast dynamically using multi-layered Web Audio nodes */
export function playRealisticCannon(volume: number, rate = 1.0) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;

  // 1. Distortion curve to simulate raw explosion pressure saturation
  const shaper = audioCtx.createWaveShaper();
  const makeDistortionCurve = (amount = 20) => {
    const k = amount;
    const n_samples = 44100;
    const curve = new Float32Array(n_samples);
    const deg = Math.PI / 180;
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  };
  shaper.curve = makeDistortionCurve(15);
  shaper.oversample = "4x";

  // 2. Layer A: Bass Shockwave (Low-frequency sine wave sweep)
  const bassOsc = audioCtx.createOscillator();
  bassOsc.type = "sine";
  bassOsc.frequency.setValueAtTime(140 * rate, now);
  // Extremely rapid drop to simulate the shockwave expansion
  bassOsc.frequency.exponentialRampToValueAtTime(25 * rate, now + 0.18 / rate);

  const bassGain = audioCtx.createGain();
  bassGain.gain.setValueAtTime(1.0, now);
  bassGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35 / rate);

  bassOsc.connect(bassGain);

  // 3. Layer B: Muzzle Blast (White noise burst filtered with a low-pass/band-pass combination)
  const bufferSize = Math.max(100, Math.floor(audioCtx.sampleRate * (0.35 / rate)));
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    // Noise shaped with exponential decay to mimic combustion envelope
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.22));
  }
  const noiseSource = audioCtx.createBufferSource();
  noiseSource.buffer = buffer;

  const noiseFilter = audioCtx.createBiquadFilter();
  noiseFilter.type = "lowpass";
  // Filter sweeps down to cut high-end "hiss", leaving the thunderous body
  noiseFilter.frequency.setValueAtTime(450 * rate, now);
  noiseFilter.frequency.exponentialRampToValueAtTime(70 * rate, now + 0.35 / rate);
  noiseFilter.Q.value = 1.0;

  const noiseGain = audioCtx.createGain();
  noiseGain.gain.setValueAtTime(0.9, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35 / rate);

  noiseSource.connect(noiseFilter);
  noiseFilter.connect(noiseGain);

  // 4. Layer C: Initial Gunpowder Crack (High-frequency transient)
  const crackBuffer = audioCtx.createBuffer(1, Math.floor(audioCtx.sampleRate * (0.05 / rate)), audioCtx.sampleRate);
  const crackData = crackBuffer.getChannelData(0);
  for (let i = 0; i < crackData.length; i++) {
    crackData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (crackData.length * 0.15));
  }
  const crackSource = audioCtx.createBufferSource();
  crackSource.buffer = crackBuffer;

  const crackFilter = audioCtx.createBiquadFilter();
  crackFilter.type = "highpass";
  crackFilter.frequency.setValueAtTime(1000 * rate, now);

  const crackGain = audioCtx.createGain();
  crackGain.gain.setValueAtTime(0.5, now);
  crackGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05 / rate);

  crackSource.connect(crackFilter);
  crackFilter.connect(crackGain);

  // 5. Master combination and gain control
  const masterGain = audioCtx.createGain();
  masterGain.gain.value = volume * globalSfxVolume * 1.2; // Slightly boosted for extra punch

  // Bass connects directly to master (clean low end)
  bassGain.connect(masterGain);

  // Muzzle noise and crack go through the waveshaper distortion to add saturation/explosiveness
  noiseGain.connect(shaper);
  crackGain.connect(shaper);
  shaper.connect(masterGain);

  // Output to speaker
  masterGain.connect(audioCtx.destination);

  // Start playback
  bassOsc.start(now);
  bassOsc.stop(now + 0.35 / rate);
  noiseSource.start(now);
  noiseSource.stop(now + 0.35 / rate);
  crackSource.start(now);
  crackSource.stop(now + 0.05 / rate);
}
